import * as THREE from "three";
import { mouse } from "@/scripts/utils/mouse";
import { prefersReducedMotion, varGap } from "@/scripts/utils/variables";

export function createShaderMaterial(texture: THREE.Texture, meshAspect: number, imageAspect: number, isLinearSource: boolean = false) {
    return new THREE.ShaderMaterial({
        uniforms: {
            uTexture: { value: texture },
            uMeshAspect: { value: meshAspect },
            uImageAspect: { value: imageAspect },
            uScrollVelocity: { value: 0 },
            uTime: { value: 0 },
            uOpacity: { value: 1 },
            uMouse: { value: new THREE.Vector2(0.5, 0.5) },
            uCursor: { value: new THREE.Vector2(0, 0) },
            uCursorStrength: { value: 0 },
            uMouseStrength: { value: 0.005 },
            uTiltStrength: { value: 0.03 },
            uParallax: { value: 0.5 },
            uParallaxSpeed: { value: 0.07 },
            uCurveStrength: { value: 0.7 },
            uSlideBend: { value: 0 },
            uSlideCurveStrength: { value: 0 },
            uParallaxX: { value: 0.5 },
            uParallaxSpeedX: { value: 0 },
            uResolution: {
                value: new THREE.Vector2(window.innerWidth, window.innerHeight),
            },
            uSquircleProgress: { value: 0.7 },
            uMaxRadiusPow: { value: 64 },
            uSharpCorners: { value: 0 },
            uChromaticAberration: { value: 0.5 },
            uLensDistortion: { value: 0.6 },
            uFocusFactor: { value: 0.85 },
            uRoll: { value: 0 },
            uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
            uGap: { value: Math.max(varGap, 1) },
            uEdgeBump: { value: 0 },
            uScrollLip: { value: 0 },

            uMirrorEnabled: { value: 0 },
            uMirrorRadius: { value: 0.8 },
            uMirrorSpeed: { value: 0.6 },
        },

        vertexShader: /* glsl */ `
          uniform float uScrollVelocity;
          uniform float uCurveStrength;
          uniform float uSlideBend;
          uniform float uSlideCurveStrength;

          varying vec2 vUv;

          void main() {
            vUv = uv;

            vec3 newPosition = position;

            vec4 clipPos = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
            vec2 ndc = clipPos.xy / clipPos.w;

            // Distance from nearest screen corner, squared for smooth falloff
            float cornerDist = length(max(abs(ndc) - vec2(0.0), 0.0));
            float curvature = cornerDist * cornerDist * (uCurveStrength / 10.0);

            // Scale curvature by scroll velocity
            float velocityFactor = abs(uScrollVelocity);

            // Base scroll curve
            float scrollCurve = curvature * velocityFactor;

            float slideCurve = ndc.x * ndc.x * (uSlideCurveStrength / 10.0) * uSlideBend;

            newPosition.z -= scrollCurve;
            vec4 scrollClipPos = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);

            newPosition.z -= slideCurve;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
            gl_Position.x = scrollClipPos.x / scrollClipPos.w * gl_Position.w;
          }
        `,

        fragmentShader: /* glsl */ `
          uniform sampler2D uTexture;
          uniform float uMeshAspect;
          uniform float uImageAspect;
          uniform float uOpacity;
          uniform vec2 uMouse;
          uniform vec2 uCursor;
          uniform float uCursorStrength;
          uniform float uMouseStrength;
          uniform float uTiltStrength;
          uniform float uParallax;
          uniform float uParallaxSpeed;
          uniform float uParallaxX;
          uniform float uParallaxSpeedX;
          uniform float uSquircleProgress;
          uniform float uMaxRadiusPow;
          uniform float uSharpCorners;
          uniform float uChromaticAberration;
          uniform float uLensDistortion;
          uniform float uFocusFactor;
          uniform float uRoll;
          uniform vec2 uResolution;
          uniform float uPixelRatio;
          uniform float uGap;
          uniform float uEdgeBump;
          uniform float uScrollLip;
          uniform float uTime;

          uniform float uMirrorEnabled;
          uniform float uMirrorRadius;
          uniform float uMirrorSpeed;

          varying vec2 vUv;

          vec2 coverUv(vec2 uv) {
            vec2 s = vec2(1.0);

            if (uImageAspect > uMeshAspect) {
              s.x = uMeshAspect / uImageAspect;
            } else {
              s.y = uImageAspect / uMeshAspect;
            }

            return (uv - 0.5) * s + 0.5;
          }

          vec2 safeUv(vec2 uv) {
            return clamp(uv, vec2(0.002), vec2(0.998));
          }

          float superellipse(vec2 p, float n) {
              p = abs(p);

              return pow(
                  pow(p.x, n) +
                  pow(p.y, n),
                  1.0 / n
              ) - 1.0;
          }

          vec2 meshExtent() {
              return uMeshAspect > 1.0 ? vec2(uMeshAspect, 1.0) : vec2(1.0, 1.0 / uMeshAspect);
          }

          float squircleBoxSDF(vec2 p, float radius) {
              vec2 extent = meshExtent();
              vec2 q = abs(p) * extent - (extent - radius);
              vec2 c = pow(max(q, vec2(0.0)) / max(radius, 0.0001), vec2(4.0));
              return radius * pow(c.x + c.y, 0.25) + min(max(q.x, q.y), 0.0) - radius;
          }

          float currentExponent() {
              float t = smoothstep(0.0, 1.0, uSquircleProgress - abs(uRoll) * 0.1 - uScrollLip * 0.08);

              // Keeps the squircle looking round for longer
              t = pow(t, 3.);

              return mix(4.0, uMaxRadiusPow, t);
          }

          float currentRadius() {
              return 0.6 * (1.0 - smoothstep(0.75, 1.0, uSquircleProgress - abs(uRoll) * 0.1 - uScrollLip * 0.08));
          }

          float currentSDF(vec2 p) {
              // Original superellipse
              float squircle = superellipse(p, currentExponent());

              float squircleBox = squircleBoxSDF(p, currentRadius());

              return mix(squircle, squircleBox, uSharpCorners);
          }

          float edgeZone(vec2 p) {
              vec2 fw = max(fwidth(p * meshExtent()), vec2(0.00001));
              float unit = (fw.x + fw.y) * 0.5;

              return uGap * uPixelRatio * unit * max(1.0 + uEdgeBump + uScrollLip, 0.3);
          }

          // Rotate uv around a center point by angle (radians)
          vec2 rotateUv(vec2 uv, vec2 center, float angle) {
              vec2 p = uv - center;
              float s = sin(angle);
              float c = cos(angle);
              return vec2(p.x * c - p.y * s, p.x * s + p.y * c) + center;
          }

          void main() {
            // Map UVs to [-1, 1] for SDF calculations
            vec2 p = vUv * 2.0 - 1.0;

            // ============================
            //  1. SQUIRCLE  /  BOX  MASK
            // ============================

            float sdf = currentSDF(p);

            // Anti-aliased alpha mask
            float fw = fwidth(sdf);
            float mask = 1.0 - smoothstep(0.0, fw * 3.0, sdf);

            // Early discard fully transparent fragments
            if (mask < 0.001) discard;

            // ============================
            //  2. TUBE CURVATURE
            // ============================

            float corner = clamp(abs(p.x) * abs(p.y), 0.0, 1.0);
            float curve = uLensDistortion * 0.06;
            float zone = edgeZone(p);
            float lip = pow(smoothstep(-zone, 0.0, squircleBoxSDF(p, max(currentRadius(), zone * 2.0))), 2.0);
            vec2 radial = length(vUv - 0.5) > 0.0001 ? normalize(vUv - 0.5) : vec2(0.0);
            vec2 toCursor = p - uCursor;
            float nearCursor = uCursorStrength * exp(-dot(toCursor, toCursor) * 8.0);
            float stretch = lip * zone * uLensDistortion * 3.0 * (1.0 + nearCursor * 2.0);

            vec2 mouse = (uMouse - 0.5) * 2.0;
            float parallax = (uParallax - 0.5) * 2.0 * uParallaxSpeed;

            vec2 c = vUv - 0.5 - mouse * uMouseStrength;
            c *= 1.0 + curve * (c.yx * c.yx) * 4.0;
            c /= 1.0 + curve * 1.2;
            c -= radial * stretch;
            c /= 1.0 - dot(c, mouse) * uTiltStrength;
            c *= 1.0 - uMouseStrength * 2.0;
            c *= 1.0 - 2.0 * uParallaxSpeedX;
            c.x += (uParallaxX - 0.5) * 2.0 * uParallaxSpeedX;

            // ============================
            //  3. CHROMATIC ABERRATION
            // ============================
            vec2 split = c * dot(c, c) * (curve * uChromaticAberration * 0.6) + radial * stretch * uChromaticAberration * 0.4 * (1.0 + uScrollLip);
            split.x += uRoll * 0.012;

            vec2 uv_R = safeUv(coverUv(0.5 + c + split + vec2(0.0, parallax)));
            vec2 uv_G = safeUv(coverUv(0.5 + c + vec2(0.0, parallax)));
            vec2 uv_B = safeUv(coverUv(0.5 + c - split + vec2(0.0, parallax)));
            vec2 uv = uv_G;

            // ============================
            //  4. SAMPLE  &  COMPOSE
            // ============================
            vec3 color = vec3(
              texture2D(uTexture, uv_R).r,
              texture2D(uTexture, uv_G).g,
              texture2D(uTexture, uv_B).b
            );

            // ============================
            //  5. ROTATING MIRROR
            // ============================
            if (uMirrorEnabled > 0.5) {
              vec2 circleP = vec2(p.x * uMeshAspect, p.y);
              float distFromCenter = length(circleP);
              float circleEdge = fwidth(distFromCenter);
              float circleMask = 1.0 - smoothstep(uMirrorRadius - circleEdge, uMirrorRadius + circleEdge, distFromCenter);

              float angle = uTime * uMirrorSpeed;

              vec2 mirrorUv = uv;

              // move origin to center
              vec2 p = mirrorUv - 0.5;

              // undo aspect so rotation happens in square space
              p.x *= uMeshAspect;

              // rotate
              float s = sin(angle);
              float c = cos(angle);
              p = vec2(
                  c * p.x - s * p.y,
                  s * p.x + c * p.y
              );

              // restore aspect
              p.x /= uMeshAspect;

              mirrorUv = safeUv(p + 0.5);
              vec3 mirrorColor = texture2D(uTexture, mirrorUv).rgb;

              color = mix(color, mirrorColor, circleMask);
            }

            float vignette = smoothstep(-0.6, 0.0, sdf) * mix(0.5, 1.0, corner);
            color *= 1.0 - vignette * 0.2;


            float glow = exp(-abs(sdf) * 24.0);
            color += glow * (color * 0.22 + vec3(0.015, 0.017, 0.015));

            gl_FragColor = vec4(color, mask * uOpacity);
            ${isLinearSource ? "#include <tonemapping_fragment>\n#include <colorspace_fragment>" : ""}
          }
        `,

        transparent: true,
    });
}

export function syncCursorWarp(mat: THREE.ShaderMaterial, rect: DOMRect): void {
    const x = (mouse.x - rect.left) / rect.width;
    const y = (mouse.y - rect.top) / rect.height;
    const isInside = x >= 0 && x <= 1 && y >= 0 && y <= 1;
    const cursor = mat.uniforms.uCursor.value as THREE.Vector2;
    const strength = mat.uniforms.uCursorStrength;

    cursor.x += (x * 2 - 1 - cursor.x) * 0.2;
    cursor.y += (1 - y * 2 - cursor.y) * 0.2;
    strength.value += ((isInside && !prefersReducedMotion ? 1 : 0) - strength.value) * 0.1;
}
